import { colors } from '@apollo/brand';
import { customColors } from '@apollo/orbit';
import _ from 'lodash';
import { useMemo } from 'react';

import { ScalarTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { zeroPadTimeSeriesData } from 'src/components/charts/helpers/zeroPadTimeSeriesData';
import { LineChartStackConfig } from 'src/components/charts/LineChart';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { FetchesOverTimeResultFragment } from 'src/lib/graphqlTypes/types';

export type FetchRequestsMetricType = 'fetchCount' | 'fetchCountPerMin';
type FetchesOverTimeResultRecord = FetchesOverTimeResultFragment['records'][0];

export function useFetchRequestsChartStacks({
  result,
  metricType,
  resolution,
}: {
  result?: FetchesOverTimeResultFragment;
  metricType: FetchRequestsMetricType;
  resolution: GraphQLTypes.Resolution;
}) {
  const getValue = useMemo(() => {
    switch (metricType) {
      case 'fetchCount':
        return (record: FetchesOverTimeResultRecord) =>
          record.data.fetchCount ?? 0;
      case 'fetchCountPerMin':
        return (record: FetchesOverTimeResultRecord) =>
          record.data.fetchCountPerMin ?? 0;
      default:
        return assertUnreachable(metricType);
    }
  }, [metricType]);
  const normalizedData: ScalarTimeSeriesData<number>[] = useMemo(
    () =>
      result
        ? zeroPadTimeSeriesData({
            data: result.records,
            timeFrom: new Date(result.roundedDownFrom),
            timeTo: new Date(result.roundedUpTo),
            resolution,
            getValue,
            getZeroFillValue: () => 0,
          })
        : [],
    [getValue, resolution, result],
  );

  const stacks = useMemo(
    () => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        label: metricType === 'fetchCount' ? 'Fetches' : 'Fetch Rate',
        unit: metricType === 'fetchCountPerMin' ? '/min' : undefined,
        data: normalizedData,
        hasData: () => true,
        getValue: (datapoint: ScalarTimeSeriesData<number>) => datapoint,
      },
    ],
    [metricType, normalizedData],
  );

  const fetchRequestsChartStackInfo = useStackInfo<
    ScalarTimeSeriesData<number>,
    ScalarTimeSeriesData<number>,
    LineChartStackConfig<ScalarTimeSeriesData<number>>
  >(stacks);

  return {
    fetchRequestsChartStackInfo,
  };
}
